import React, { useState, useEffect } from "react";
import {
  Modal,
  Typography,
  Button,
  Row,
  Col,
  Steps,
  Progress,
  Card,
} from "antd";
import { useDispatch } from "react-redux";
import { fireConfetti } from "@utils/confetti";
import { saveUserOnboardingData } from "@api/user/user";
import { logoutAction } from "@redux/actions/authActions";
import { motion } from "framer-motion";
import logo from "@assets/images/logo/logo.png";
import SubscriptionModal from "./SubscriptionModal";
import {
  CodeOutlined,
  MedicineBoxOutlined,
  BookOutlined,
  DollarOutlined,
  ShoppingOutlined,
  SettingOutlined,
  QuestionOutlined,
  TeamOutlined,
  RocketOutlined,
  BulbOutlined,
  DashboardOutlined,
  UserOutlined,
  FireOutlined,
  SearchOutlined,
  MailOutlined,
  EditOutlined,
  FileTextOutlined,
  NotificationOutlined,
  LineChartOutlined,
  ShoppingCartOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import "@assets/css/OnboardingModal.css";

const { Step } = Steps;
const { Title, Paragraph } = Typography;

// Local storage key
const ONBOARDING_COMPLETE_KEY = "copilotly_onboarding_complete";

// Enhanced Data Arrays with icons
const industry = [
  {
    id: 1,
    name: "Technology",
    icon: <CodeOutlined />,
    color: "#3498db",
    description: "Software, IT, SaaS",
  },
  {
    id: 2,
    name: "Healthcare",
    icon: <MedicineBoxOutlined />,
    color: "#e74c3c",
    description: "Hospitals, Clinics, Health Tech",
  },
  {
    id: 3,
    name: "Education",
    icon: <BookOutlined />,
    color: "#f39c12",
    description: "Schools, Universities, EdTech",
  },
  {
    id: 4,
    name: "Finance",
    icon: <DollarOutlined />,
    color: "#2ecc71",
    description: "Banking, Insurance, FinTech",
  },
  {
    id: 5,
    name: "Retail",
    icon: <ShoppingOutlined />,
    color: "#9b59b6",
    description: "Stores, E-commerce, Retail Tech",
  },
  {
    id: 6,
    name: "Manufacturing",
    icon: <SettingOutlined />,
    color: "#34495e",
    description: "Production, Factories, Supply Chain",
  },
  {
    id: 7,
    name: "Other",
    icon: <QuestionOutlined />,
    color: "#7f8c8d",
    description: "Other industries not listed",
  },
];

const department = [
  {
    id: 0,
    name: "I'm a Student",
    icon: <UserOutlined />,
    color: "#3498db",
    description: "Studying or researching",
  },
  {
    id: 1,
    name: "Engineering",
    icon: <CodeOutlined />,
    color: "#2ecc71",
    description: "Development & technical teams",
  },
  {
    id: 2,
    name: "Marketing",
    icon: <RocketOutlined />,
    color: "#e74c3c",
    description: "Brand, Growth & Communications",
  },
  {
    id: 3,
    name: "Human Resources",
    icon: <TeamOutlined />,
    color: "#9b59b6",
    description: "People, Talent & Culture",
  },
  {
    id: 4,
    name: "Sales",
    icon: <FireOutlined />,
    color: "#f39c12",
    description: "Revenue & Business Development",
  },
  {
    id: 5,
    name: "Finance",
    icon: <DollarOutlined />,
    color: "#1abc9c",
    description: "Accounting & Financial Planning",
  },
  {
    id: 6,
    name: "Operations",
    icon: <DashboardOutlined />,
    color: "#34495e",
    description: "Process & Business Operations",
  },
  {
    id: 7,
    name: "Other",
    icon: <BulbOutlined />,
    color: "#7f8c8d",
    description: "Other departments not listed",
  },
];

const employeeRanges = [
  { value: "1", label: "Just me", icon: <UserOutlined />, color: "#3498db" },
  { value: "2-50", label: "2 - 50", icon: <TeamOutlined />, color: "#2ecc71" },
  { value: "51-100", label: "51 - 100", icon: <TeamOutlined />, color: "#9b59b6" },
  { value: "101-500", label: "101 - 500", icon: <TeamOutlined />, color: "#f39c12" },
  { value: "501-1000", label: "1000+", icon: <TeamOutlined />, color: "#e74c3c" },
];

const categories = [
  {
    name: "Search",
    icon: <SearchOutlined />,
    color: "#3498db",
    description: "AI-powered web search",
  },
  {
    name: "Email",
    icon: <MailOutlined />,
    color: "#2ecc71",
    description: "Compose and manage emails",
  },
  {
    name: "Writing",
    icon: <EditOutlined />,
    color: "#e74c3c",
    description: "Content creation & editing",
  },
  {
    name: "Blog",
    icon: <FileTextOutlined />,
    color: "#9b59b6",
    description: "Blog posts & articles",
  },
  {
    name: "Ads",
    icon: <NotificationOutlined />,
    color: "#f39c12",
    description: "Ad copy & campaigns",
  },
  {
    name: "Marketing",
    icon: <RocketOutlined />,
    color: "#1abc9c",
    description: "Marketing content",
  },
  {
    name: "Sales",
    icon: <LineChartOutlined />,
    color: "#34495e",
    description: "Sales outreach & pitches",
  },
  {
    name: "All of these",
    icon: <AppstoreOutlined />,
    color: "#8B5CF6",
    description: "Use all features",
  },
];

const OnboardingModal = ({ visible, onClose, onComplete, user }) => {
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // User selections
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Subscription modal state
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [onboardingModalVisible, setOnboardingModalVisible] = useState(false);

  // Check local storage on mount and initialize visibility
  useEffect(() => {
    if (visible) {
      // If onboarding is already marked as complete in localStorage, don't show the modal
      if (localStorage.getItem(ONBOARDING_COMPLETE_KEY) === "true") {
        // Skip onboarding and check if subscription is needed
        if (!user?.subscribe_complete && !user?.appSumoUser) {
          setSubscriptionModalVisible(true);
        } else {
          // User already completed both onboarding and subscription
          onComplete();
        }
      } else {
        // Show onboarding modal if not completed
        setCurrentStep(0);
        setOnboardingModalVisible(true);
      }
    }
  }, [visible, user, onComplete]);

  // Handle logout
  const handleLogout = () => {
    dispatch(logoutAction());
  };

  // Handle step navigation
  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Handle selections
  const handleIndustrySelect = (industryName) => {
    setSelectedIndustry(industryName);
  };

  const handleDepartmentSelect = (departmentName) => {
    setSelectedDepartment(departmentName);
  };

  const handleEmployeesSelect = (employeeCount) => {
    setSelectedEmployees(employeeCount);
  };

  // Toggle category selection
  const toggleCategory = (categoryName) => {
    if (selectedCategories.includes(categoryName)) {
      setSelectedCategories(selectedCategories.filter((item) => item !== categoryName));
    } else {
      setSelectedCategories([...selectedCategories, categoryName]);
    }
  };

  // Mark onboarding as complete in localStorage
  const markOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
  };

  // Handle onboarding completion
  const handleComplete = async () => {
    // Fire confetti when completing the onboarding
    fireConfetti();

    setLoading(true);

    // Gather onboarding data
    const onboardingData = {
      about: [
        {
          industry: selectedIndustry,
          department: selectedDepartment,
          employeeCount: selectedEmployees,
        },
      ],
      categories: selectedCategories,
    };

    try {
      await saveUserOnboardingData(onboardingData);

      // Mark onboarding as complete in localStorage
      markOnboardingComplete();

      // Hide the onboarding modal
      setOnboardingModalVisible(false);

      // Only show subscription if user isn't an AppSumo user
      // and hasn't already subscribed
      if (!user?.subscribe_complete && !user?.appSumoUser) {
        setSubscriptionModalVisible(true);
      } else {
        // If user is AppSumo or already subscribed, just complete
        onComplete();
      }
    } catch (error) {
      console.error("Error saving onboarding data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Skip onboarding entirely
  const handleSkip = async () => {
    setLoading(true);

    // Minimal data to mark onboarding as complete
    const onboardingData = {
      about: [
        {
          industry: "",
          department: "",
          employeeCount: "",
        },
      ],
      categories: [],
    };

    try {
      await saveUserOnboardingData(onboardingData);

      // Mark onboarding as complete in localStorage
      markOnboardingComplete();

      // Hide the onboarding modal
      setOnboardingModalVisible(false);

      // Only show subscription if user isn't an AppSumo user
      // and hasn't already subscribed
      if (!user?.subscribe_complete && !user?.appSumoUser) {
        setSubscriptionModalVisible(true);
      } else {
        // If user is AppSumo or already subscribed, just complete
        onComplete();
      }
    } catch (error) {
      console.error("Error saving onboarding data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle subscription modal completion
  const handleSubscriptionComplete = () => {
    setSubscriptionModalVisible(false);
    onComplete();
  };

  // Check if the current step can proceed
  const canProceed = () => {
    switch (currentStep) {
      case 0: // Industry, Department & Employees
        return selectedIndustry && selectedDepartment && selectedEmployees;
      case 1: // Categories
        return selectedCategories.length > 0;
      default:
        return true;
    }
  };

  // Define steps
  const steps = [
    {
      title: "About",
      content: (
        <div className="step-content about-step">
          <Title level={4} className="step-title">
            <span className="gradient-text">Tell us about yourself</span>
          </Title>
          <Paragraph className="step-description">
            Help us personalize your experience
          </Paragraph>

          <div className="selection-section">
            <Title level={5} className="section-title">
              Industry
            </Title>

            <div className="card-grid">
              <Row gutter={[12, 10]}>
                {industry.map((item) => (
                  <Col xs={12} sm={8} md={6} key={item.id}>
                    <Card
                      className={`selection-card ${
                        selectedIndustry === item.name ? "selected" : ""
                      }`}
                      onClick={() => handleIndustrySelect(item.name)}
                    >
                      <div
                        className="card-icon"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.icon}
                      </div>
                      <div className="card-title">{item.name}</div>
                      <div className="card-description">
                        {item.description}
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </div>

          <div className="selection-section">
            <Title level={5} className="section-title">
              Role
            </Title>

            <div className="card-grid">
              <Row gutter={[12, 10]}>
                {department.map((item) => (
                  <Col xs={12} sm={8} md={6} key={item.id}>
                    <Card
                      className={`selection-card ${
                        selectedDepartment === item.name ? "selected" : ""
                      }`}
                      onClick={() => handleDepartmentSelect(item.name)}
                    >
                      <div
                        className="card-icon"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.icon}
                      </div>
                      <div className="card-title">{item.name}</div>
                      <div className="card-description">
                        {item.description}
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </div>

          <div className="selection-section">
            <Title level={5} className="section-title">
              Team Size
            </Title>

            <div className="card-grid">
              <Row gutter={[12, 10]}>
                {employeeRanges.map((item) => (
                  <Col xs={12} sm={8} md={6} key={item.value}>
                    <Card
                      className={`selection-card ${
                        selectedEmployees === item.value ? "selected" : ""
                      }`}
                      onClick={() => handleEmployeesSelect(item.value)}
                    >
                      <div
                        className="card-icon"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.icon}
                      </div>
                      <div className="card-title">{item.label}</div>
                      <div className="card-description">employees</div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Usage",
      content: (
        <div className="step-content purpose-step">
          <Title level={4} className="step-title">
            <span className="gradient-text">
              How will you use Copilotly?
            </span>
          </Title>
          <Paragraph className="step-description">
            Select all that apply. You can change this later.
          </Paragraph>

          <div className="selection-section">
            <div className="card-grid purpose-grid">
              <Row gutter={[12, 10]}>
                {categories.map((item, index) => (
                  <Col xs={12} sm={8} md={6} key={index}>
                    <Card
                      className={`feature-card ${
                        selectedCategories.includes(item.name) ? "selected" : ""
                      }`}
                      onClick={() => toggleCategory(item.name)}
                    >
                      <div
                        className="card-icon"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.icon}
                      </div>
                      <div className="card-title">{item.name}</div>
                      <div className="card-description">
                        {item.description}
                      </div>
                      {selectedCategories.includes(item.name) && (
                        <div className="feature-check-icon">✓</div>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Overlay */}
      {onboardingModalVisible && <div className="modal-overlay" />}

      {/* Main Onboarding Modal */}
      <Modal
        visible={onboardingModalVisible}
        onCancel={onClose}
        footer={null}
        width={800}
        centered
        className="copilotly-modal onboarding-modal"
        maskClosable={false}
        closable={false}
        destroyOnClose={true}
      >
        <div className="copilotly-modal-content">
          {/* Creative Header with Logo */}
          <div className="modal-header">
            <div className="header-content">
              <motion.img
                src={logo}
                alt="Copilotly"
                height={60}
                className="header-logo"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              />
              <motion.div
                className="header-text"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <h2 className="header-title">Welcome to Copilotly</h2>
                <p className="header-subtitle">
                  Let's personalize your AI copilot experience
                </p>
              </motion.div>
            </div>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            >
              <Progress
                type="circle"
                percent={Math.round(((currentStep + 1) / steps.length) * 100)}
                width={50}
                strokeColor={{
                  "0%": "#7B61FF",
                  "100%": "#A78BFA",
                }}
                trailColor="rgba(255, 255, 255, 0.3)"
                format={(percent) => (
                  <span style={{ color: "white", fontSize: "14px" }}>
                    {percent}%
                  </span>
                )}
              />
            </motion.div>
          </div>

          {/* Scrollable Content Area */}
          <div className="modal-scrollable-content">
            {/* Steps header */}
            <div className="steps-header">
              <Steps
                current={currentStep}
                className="copilotly-steps-indicator"
                progressDot
              >
                {steps.map((step, index) => (
                  <Step key={index} title={step.title} />
                ))}
              </Steps>
            </div>

            {/* Current step content */}
            <motion.div
              className="step-container"
              key={currentStep}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {steps[currentStep].content}
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <motion.div
          className="modal-footer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="footer-buttons">
            <Button
              className="skip-button"
              onClick={handleSkip}
              loading={loading}
            >
              Skip setup
            </Button>

            <div className="navigation-buttons">
              {currentStep > 0 && (
                <Button onClick={prevStep} className="back-button">
                  Back
                </Button>
              )}

              {currentStep < steps.length - 1 ? (
                <Button
                  type="primary"
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className="next-button"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleComplete}
                  loading={loading}
                  disabled={!canProceed()}
                  className="complete-button"
                >
                  Complete Setup
                </Button>
              )}
            </div>
          </div>

          <div className="step-counter">
            Step {currentStep + 1} of {steps.length}
          </div>
        </motion.div>
      </Modal>

      {/* Subscription Modal */}
      {subscriptionModalVisible && (
        <SubscriptionModal
          visible={subscriptionModalVisible}
          onClose={handleSubscriptionComplete}
          user={user}
        />
      )}
    </>
  );
};

export default OnboardingModal;
